import {UIComponent} from "@webfruits/core";
import {Color} from "../../../style/Color";
import {TextField} from "@webfruits/toolbox/dist/components/text/TextField";
import {Font} from "../../../style/Font";
import {QueryStylesController} from "@webfruits/toolbox/dist/controller/style/QueryStylesController";
import {gsap} from "gsap";
import {Hotspot} from "../../illustration/hotspots/Hotspot";
import {AppConfig} from "../../../config/AppConfig";
import {SVGComponent} from "@webfruits/toolbox/dist/components/svg/SVGComponent";
import {CoreAssets} from "../../../core/CoreAssets";

/******************************************************************
 * FactInfo extends UIComponent
 *
 * @author matthias.schulz@jash.de
 *****************************************************************/

export class FactInfo extends UIComponent {

    /******************************************************************
     * Properties
     *****************************************************************/

    private _titleField: TextField;
    private _descField: TextField;
    private _sourceField: TextField;
    private _additionalDescStyles: QueryStylesController;
    private _contentFrame: UIComponent;
    private _currentHotspot: Hotspot;
    private _showTimeoutID: number;
    private _isShowing: boolean = true;

    /******************************************************************
     * Constructor
     *****************************************************************/

    constructor() {
        super("fact-info");
        this.initContentFrame();
        this.initTitleField();
        this.initDescField();
        this.initSourceField();
        this.hide(true);
    }

    /******************************************************************
     * Public Methodes
     *****************************************************************/

    get width(): number {
        return this._contentFrame.view.scrollWidth;
    }

    get height(): number {
        return this._contentFrame.view.scrollHeight;
    }

    public setHotspot(hotspot: Hotspot) {
        if (!hotspot || (!AppConfig.USE_FACT_FOR_INFO && hotspot.factTitle == "")) {
            this._currentHotspot = null;
            this.hide();
        } else {
            if (this._currentHotspot == hotspot) {
                return;
            }
            this._currentHotspot = hotspot;
            this.updateVisibility();
        }
    }

    public updateVisibility() {
        clearTimeout(this._showTimeoutID);
        this._showTimeoutID = window.setTimeout(() => {
            this.show();
        }, AppConfig.FACT_INFO_SHOW_DELAY * 1000)
    }

    public updateStyles() {
        this.applyStyle({
            position: "absolute",
        })
        this._contentFrame.applyStyle({
            transformOrigin: "top left",
            width: AppConfig.USE_FACT_FOR_INFO ? 275 : 225,
            boxSizing: "border-box",
            padding: 20,
            borderRadius: 6,
            backgroundColor: Color.DARK_BLUE + "CC",
            color: Color.WHITE,
            overflow: "hidden",
        })
        this._titleField.applyStyle({
            hyphens: "auto"
        })
        this._descField.applyStyle({
            hyphens: "auto"
        })
        this._sourceField.applyStyle({
            display: AppConfig.USE_FACT_FOR_INFO ? "none" : "block"
        })
        this._additionalDescStyles.to("strong", {
            ...Font.P_SMALL_STRONG_FONTSTYLE
        })
        this._additionalDescStyles.to("span.arrow", {
            display: "inline-block",
            width: 15,
            y: 1,
            position: "relative",
            marginRight: "0.5em"
        })
    }

    /******************************************************************
     * Private Methodes
     *****************************************************************/

    private initContentFrame() {
        this._contentFrame = new UIComponent("content-frame");
        this.addChild(this._contentFrame);
    }

    private initTitleField() {
        this._titleField = new TextField({fontStyle: () => Font.H2_FONTSTYLE});
        this._contentFrame.addChild(this._titleField);
    }

    private initDescField() {
        this._descField = new TextField({fontStyle: () => Font.P_SMALL_FONTSTYLE});
        this._additionalDescStyles = new QueryStylesController(this._descField.view);
        this._contentFrame.addChild(this._descField);
    }

    private initSourceField() {
        this._sourceField = new TextField({fontStyle: () => Font.FOOTNOTE_FONTSTYLE});
        this._contentFrame.addChild(this._sourceField);
    }

    private hide(instantly: boolean = false) {
        if (!this._isShowing) return;
        this._isShowing = false;
        gsap.killTweensOf(this._contentFrame.view);
        gsap.to(this._contentFrame.view, {
            duration: instantly ? 0 : 0.2,
            alpha: 0,
            scale: 0.7,
            ease: "power2.out"
        })
    }

    private show() {
        if (!this._currentHotspot) return;
        if (this._isShowing) return;
        this._isShowing = true;
        this.updateContent();
        gsap.killTweensOf(this._contentFrame.view);
        gsap.to(this._contentFrame.view, {
            duration: 0.7,
            alpha: 1,
            scale: 1,
            ease: "elastic.out(1.2,0.8)"
        })
    }

    private updateContent() {
        const hotspot = this._currentHotspot;
        this._titleField.html = AppConfig.USE_FACT_FOR_INFO ? hotspot.title : hotspot.factTitle;
        this._descField.html = AppConfig.USE_FACT_FOR_INFO ? hotspot.desc : hotspot.factDesc;
        this._sourceField.html = AppConfig.USE_FACT_FOR_INFO ? "" : hotspot.factSource;
        const arrowFrame = this._descField.view.querySelectorAll(".arrow")[0];
        if (arrowFrame) {
            const descArrow = new SVGComponent(CoreAssets.DESC_ARROW);
            descArrow.clearSizeDefinition();
            descArrow.getElementByID("arrow").getElementsByTagName("polygon")[0].style.fill = Color.WHITE;
            arrowFrame.appendChild(descArrow.view);
        }
        this.updateStyles();
    }

    /******************************************************************
     * Events
     *****************************************************************/

    // no events yet

}
